// src/components/NewTicketForm.tsx
import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { getUserName, getVendorId } from "../../api/sessions";
import { createTicket } from "../../api/support";
import HtmlEditor, { Item, Toolbar as HTMLToolbar } from "devextreme-react/html-editor";
import { Button } from "devextreme-react";
import { useTranslation } from "react-i18next";


type Props = {
    onCreated?: (id?: string) => void;
    onSavingChange?: (saving: boolean) => void;
};

export type NewTicketFormHandle = {
    submit: () => Promise<void>;
};

export const NewTicketForm = React.forwardRef<NewTicketFormHandle, Props>(({ onCreated, onSavingChange }, forwardedRef) => {
    const vendorId = getVendorId();
    const user = getUserName();
    const { t } = useTranslation();

    const [title, setTitle] = useState("");
    const [topic, setTopic] = useState("Genel");
    const [description, setDescription] = useState("");
    const [files, setFiles] = useState<File[]>([]);
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        onSavingChange?.(saving);
    }, [saving, onSavingChange]);

    if (!vendorId) {
        return <div>{t("vendorIdRequired")}</div>;
    }

    async function handleCreate() {
        if (!title.trim()) {
            alert(t("titleRequired"));
            return;
        }
        setSaving(true);
        try {
            const created = await createTicket({
                title,
                description,
                status: 1,
                priority: 2,
                product_code: 10,
                vendor_ref: Number(vendorId) || 0,
                contact_ref: 0,
                customer_name: user ?? "",
                customer_email: "",
                attachments: [],
            });

            window.dispatchEvent(new CustomEvent("support:ticket-created", { detail: created }));
            onCreated?.(created?.ref ?? created?.ticket_code);
            setTitle("");
            setDescription("");
            setFiles([]);
        } catch (err) {
            console.error(err);
            alert(t("errorOccurred"));
        } finally {
            setSaving(false);
        }
    }

    useImperativeHandle(forwardedRef, () => ({
        submit: handleCreate,
    }), [title, topic, description, files, vendorId, user]);

    return (
        <div className="h-full min-h-0 overflow-auto">
            <div className="p-4">

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                        <label className="text-sm support-text-muted">{t("title")}</label>
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full rounded-md border px-3 py-2 text-sm outline-none support-input support-border"
                            placeholder={t("ticketTitlePlaceholder")}
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm support-text-muted">{t("subject")}</label>
                        <select
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                            className="w-full rounded-md border px-3 py-2 text-sm outline-none support-input support-border"
                        >
                            <option value="Genel">{t("general")}</option>
                            <option value="E-Fatura">{t("eInvoice")}</option>
                            <option value="EDI">{t("edi")}</option>
                            <option value="Dunning">{t("dunning")}</option>
                            <option value="Diğer">{t("others")}</option>
                        </select>
                    </div>
                </div>

                <div className="mt-4">
                    <label className="text-sm support-text-muted">{t("comment")}</label>
                    <div className="flex-1 border rounded support-border">
                        <HtmlEditor height={320} value={description} onValueChanged={(e) => setDescription(e.value)}>
                            <HTMLToolbar>
                                <Item name="undo" />
                                <Item name="redo" />
                                <Item name="separator" />
                                <Item name="bold" />
                                <Item name="italic" />
                                <Item name="strike" />
                                <Item name="underline" />
                                <Item name="separator" />
                                <Item name="alignLeft" />
                                <Item name="alignCenter" />
                                <Item name="alignRight" />
                                <Item name="alignJustify" />
                                <Item name="separator" />
                                <Item name="orderedList" />
                                <Item name="bulletList" />
                                <Item name="separator" />
                                <Item name="color" />
                                <Item name="background" />
                                <Item name="separator" />
                                <Item name="link" />
                                <Item name="image" />
                                <Item name="separator" />
                                <Item name="clear" />
                                <Item name="codeBlock" />
                                <Item name="blockquote" />
                            </HTMLToolbar>
                        </HtmlEditor>
                    </div>
                </div>

                <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex flex-col gap-1">
                        <label className="text-sm support-text-muted">{t("attachments")}</label>
                        <div className="flex items-center gap-2">
                            <input
                                id="new-ticket-attachments"
                                type="file"
                                multiple
                                onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
                                className="hidden"
                                ref={fileInputRef}
                            />
                            <Button
                                icon="attach"
                                text={t("file")}
                                stylingMode="outlined"
                                onClick={() => fileInputRef.current?.click()}
                            />
                            {!!files.length && (
                                <button
                                    type="button"
                                    className="text-sm underline support-text-muted"
                                    onClick={() => setFiles([])}
                                >
                                    {t("clear")}
                                </button>
                            )}
                        </div>
                        {!!files.length && (
                            <div className="text-xs support-text-muted">
                                {t("filesSelected", { count: files.length })}
                            </div>
                        )}
                        {!!files.length && (
                            <div className="text-xs support-text-muted">
                                {files.slice(0, 3).map((f) => f.name).join(", ")}
                                {files.length > 3 ? ` (+${files.length - 3})` : ""}
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end" />
                </div>
            </div>
        </div>
    );
});
